import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { albumService } from '../../services/api';
import { stickerCode } from '../albumData';
import { buildGoMessagesPath } from '../../utils/redirect';
import ConfirmDialog from './ConfirmDialog';

/**
 * Modal centrado para un trade en estado ACCEPTED. Muestra:
 *  - Resumen (Recibís / Das con códigos de figurita)
 *  - Info del otro usuario (nombre, comunidad/es compartidas)
 *  - Botón "Abrir chat" que va al chat interno con la otra parte
 *  - Acciones: Cancelar reserva / Marcar como completado
 *
 * NOTA: antes mostraba el teléfono (WhatsApp) o email del otro user para
 * coordinar fuera de la app. Lo sacamos en favor del chat interno —
 * misma decisión que en TradeDetailModal y en el flow post-reserva.
 */
function TradeAcceptedModal({ trade, communityNameById, onClose, onAfterAction }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState(null); // 'complete' | 'cancel' | null
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const sharedCommunities = (trade.otherUser?.communityIds || [])
    .map(id => communityNameById?.get?.(id))
    .filter(Boolean);

  const otherUserId = trade.otherUser?.id;
  const handleOpenChat = () => {
    if (!otherUserId) return;
    navigate(buildGoMessagesPath({ userId: otherUserId }));
  };

  const runComplete = async () => {
    setBusy(true);
    try {
      await albumService.completeTrade(trade.id);
      if (onAfterAction) await onAfterAction();
      setConfirm(null);
      onClose();
    } finally { setBusy(false); }
  };
  const runCancel = async () => {
    setBusy(true);
    try {
      await albumService.cancelTrade(trade.id);
      if (onAfterAction) await onAfterAction();
      setConfirm(null);
      onClose();
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full max-w-md bg-surface rounded-2xl shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-outline-variant flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0"
              style={{ backgroundColor: trade.otherUser?.color || '#10b981' }}
            >{trade.otherUser?.initial || '?'}</div>
            <div className="min-w-0">
              <p className="text-sm font-black text-on-surface truncate">{trade.otherUser?.name}</p>
              {sharedCommunities.length > 0 && (
                <p className="text-[10px] text-on-surface-variant truncate">
                  {sharedCommunities.slice(0, 2).join(' · ')}
                  {sharedCommunities.length > 2 ? ` +${sharedCommunities.length - 2}` : ''}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-surface-container"
            aria-label={t('Cerrar')}
          >
            <span className="material-symbols-outlined text-base">close</span>
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4 max-h-[60vh] overflow-y-auto">

          {/* Resumen del trade */}
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-on-surface-variant mb-2">
              {t('Resumen')}
            </p>
            <div className="space-y-2">
              <ChipRow accent="#10b981" label={t('Recibís')} nums={trade.youReceive} />
              <ChipRow accent="#ef4444" label={t('Das')} nums={trade.youGive} />
            </div>
          </div>

          {/* Coordinación — chat interno (sin teléfono ni email expuestos) */}
          <div className="rounded-xl border border-outline-variant p-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="material-symbols-outlined text-base text-primary">forum</span>
              <span className="text-[11px] font-black uppercase tracking-widest text-on-surface-variant">
                {t('Coordinen el intercambio')}
              </span>
            </div>
            {otherUserId ? (
              <>
                <button
                  type="button"
                  onClick={handleOpenChat}
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-primary text-on-primary hover:opacity-90 transition-opacity"
                >
                  <span className="material-symbols-outlined text-sm">forum</span>
                  {t('Abrir chat con {{name}}', { name: trade.otherUser?.name || t('el otro usuario') })}
                </button>
                <p className="mt-2 text-[10px] text-on-surface-variant">
                  {t('Acuerden por mensajes acá adentro cómo intercambiar las figuritas.')}
                </p>
              </>
            ) : (
              <p className="text-xs text-on-surface-variant">
                {t('Contacto no disponible en este momento.')}
              </p>
            )}
          </div>
        </div>

        {/* Footer actions */}
        <div className="px-5 py-3 border-t border-outline-variant flex items-center gap-2">
          <button
            type="button"
            onClick={() => setConfirm('cancel')}
            disabled={busy}
            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-xs font-bold border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-60"
          >
            <span className="material-symbols-outlined text-sm">cancel</span>
            {t('Cancelar reserva')}
          </button>
          <button
            type="button"
            onClick={() => setConfirm('complete')}
            disabled={busy}
            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-xs font-bold bg-primary text-on-primary disabled:opacity-60"
          >
            <span className="material-symbols-outlined text-sm">check_circle</span>
            {t('Marcar completado')}
          </button>
        </div>
      </div>

      {confirm === 'complete' && (
        <ConfirmDialog
          open
          title={t('Confirmar intercambio')}
          body={t('¿Ya intercambiaron las figuritas en persona? Esto las moverá entre los álbumes y no se puede deshacer.')}
          confirmLabel={t('Sí, completar')}
          cancelLabel={t('Todavía no')}
          variant="primary"
          onConfirm={runComplete}
          onCancel={() => setConfirm(null)}
        />
      )}
      {confirm === 'cancel' && (
        <ConfirmDialog
          open
          title={t('Cancelar reserva')}
          body={t('Las figuritas vuelven a estar disponibles para ambos. ¿Confirmás?')}
          confirmLabel={t('Sí, cancelar')}
          cancelLabel={t('Volver')}
          variant="danger"
          onConfirm={runCancel}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

function ChipRow({ accent, label, nums }) {
  if (!nums || nums.length === 0) return null;
  return (
    <div className="flex items-start gap-2">
      <span className="text-[10px] font-black uppercase tracking-widest mt-1 w-14 shrink-0" style={{ color: accent }}>
        {label}
      </span>
      <div className="flex-1 flex flex-wrap gap-1">
        {nums.map(n => (
          <span
            key={n}
            className="px-1.5 py-0.5 rounded text-[11px] font-bold tabular-nums"
            style={{ backgroundColor: `${accent}22`, color: accent }}
          >{stickerCode(n)}</span>
        ))}
      </div>
    </div>
  );
}

export default TradeAcceptedModal;
